<script lang="ts">
	import { ButtonType } from '../button'
	import Icon from '../icon/Icon.svelte'
	import { Tooltip } from '@repo/ui'
	import type { IconSource } from '@steeze-ui/heroicons'

	interface Props {
		outline?: boolean
		iconOutline?: boolean
		disabled?: boolean
		label?: string
		type?: ButtonType
		icon?: IconSource
		ariaLabel?: string | null
		full?: boolean
		href?: string | null
		active?: boolean
		target?: string | null
		name: string
		title?: string | null
		iconSmall?: boolean
		iconLarge?: boolean
		smallGap?: boolean
		small?: boolean
		stopPropagation?: boolean
		class?: string
		loading?: boolean
		onclick?: (event: Event) => void
		children?: import('svelte').Snippet
	}

	let {
		outline = true,
		iconOutline = false,
		disabled = false,
		label = '',
		type = ButtonType.button,
		icon = null,
		ariaLabel = null,
		full = false,
		href = null,
		active = false,
		target = null,
		name,
		title = null,
		iconSmall = true,
		iconLarge = false,
		smallGap = false,
		small = false,
		stopPropagation = false,
		class: className,
		loading = false,
		onclick = null,
		children,
	}: Props = $props()

	let tagName: 'a' | 'button' = $derived(href ? 'a' : 'button')

	function handleClick(event: Event) {
		if (stopPropagation) {
			event.stopPropagation()
		}
		if (onclick) {
			onclick(event)
		}
	}

	let restProps = $derived(
		tagName === 'button'
			? {
					disabled: disabled || loading || null,
					type: ButtonType[type],
					name,
				}
			: {
					href,
					target,
				},
	)
</script>

<Tooltip autoWidth disabled={!title} small>
	{#snippet placeholder()}
		<svelte:element
			this={tagName}
			class="
			flex
	    cursor-pointer items-center
	    {disabled || loading ? '' : 'hover:bg-gray-700/10 dark:hover:bg-gray-100/10'}
	    {!label && icon ? 'rounded-full' : outline ? 'rounded-md' : 'rounded-xl'}
	    {disabled
				? 'opacity-75'
				: (!label && icon) || outline
					? 'hover:shadow-[0_0_0_4px_rgba(55,65,81,0.1)] dark:hover:shadow-[0_0_0_4px_rgba(243,244,246,0.1)]'
					: ''}
	    {full ? 'w-full' : ''}
	    {active ? 'text-primary-500' : ''}
		{className}
	  "
			aria-label={ariaLabel}
			data-test={name ? `button-${name}` : null}
			onclick={handleClick}
			{...restProps}
		>
			{#if children}{@render children()}{:else}
				<div
					class="flex items-center {smallGap || small ? 'gap-x-1' : 'gap-x-2.5'} {small
						? 'text-xs'
						: 'text-base'}"
				>
					{#if icon}
						<div class="relative flex items-center">
							<Icon large={iconLarge} outline={iconOutline} small={iconSmall} src={icon} />
							{#if loading && !label}
								<div
									class="absolute inset-0 animate-spin rounded-full border-r-1 border-gray-400"
								></div>
							{/if}
						</div>
					{/if}
					{#if label}
						<span class="leading-5">
							{label}
						</span>
					{/if}
				</div>
			{/if}
		</svelte:element>
	{/snippet}
	<p class="whitespace-nowrap">
		{title}
	</p>
</Tooltip>
